import React from "react";
import { observer } from "mobx-react-lite";
import { Box, Card, CardMedia, Grid, Tooltip, Typography } from "@mui/material";
import Svg from "e.sic-platform.webclient/src/components/svg/Svg";
import { Address, TelFax } from "e.sic-platform.webclient/src/components/svg/svgImageList";
import { TermsAndConditions } from "e.sic-platform.webclient/src/components/terms-and-conditions/TermsAndConditions";
import { translationService } from "e.sic-platform.webclient/src/services/translation/TranslationService";

interface StaticInfoProps {
    svg: React.FunctionComponent;
    descriptionKey: string;
}

const StaticInfo = ({ svg, descriptionKey }: StaticInfoProps) => (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
        <Svg svg={svg} width={40} height={40} />
        <Typography
            variant="body2"
            component="pre"
            sx={{ fontFamily: "inherit", m: 0, whiteSpace: "pre-wrap", lineHeight: 1.5 }}
        >
            {translationService.translate(descriptionKey)}
        </Typography>
    </Box>
);

interface ProductLinkProps {
    title: string;
    description: string;
    image: string;
    href: string;
}

// Runtime config is loaded into sessionStorage by ConfigurationService. Read it
// defensively — if it's missing or malformed, return "" instead of throwing (a raw
// JSON.parse here would crash the whole footer render).
const configUrl = (key: "platform_url" | "inspector_url"): string => {
    try {
        const raw = sessionStorage["config-storage"];
        return raw ? JSON.parse(raw)[key] ?? "" : "";
    } catch {
        return "";
    }
};

const ProductLink = ({ title, description, image, href }: ProductLinkProps) => {
    const open = () => {
        if (!href) return;
        const win = window.open(href, "_blank", "noopener,noreferrer");
        if (win) win.opener = null;
    };
    return (
        <Tooltip title={translationService.translate(description)} placement="top" arrow>
            <Card
                elevation={3}
                onClick={open}
                role="button"
                tabIndex={0}
                aria-label={translationService.translate(title)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        open();
                    }
                }}
                sx={{
                    cursor: "pointer",
                    width: "100%",
                    maxWidth: 140,
                    height: 140,
                    display: "flex",
                    borderRadius: 2,
                    overflow: "hidden",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                        transform: "translateY(-3px)",
                        boxShadow: 10,
                    },
                    "&:focus-visible": {
                        outline: "2px solid",
                        outlineColor: "primary.main",
                        outlineOffset: 2,
                    },
                }}
            >
                <CardMedia
                    component="img"
                    image={image}
                    alt={translationService.translate(title)}
                    sx={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                />
            </Card>
        </Tooltip>
    );
};

export default observer(function HomeFooter() {
    return (
        <Box
            sx={{
                mt: 6,
                pl: { xs: 3, sm: 5, md: 8, lg: 12, xl: 16 },
                pr: { xs: 3, sm: 5, md: 8, lg: 12, xl: 16 },
                py: 4,
                backgroundColor: "rgba(33, 33, 33, 0.08)",
            }}
        >
            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        {translationService.translate("HelpPage.contact.title")}
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={4}>
                            <StaticInfo svg={Address} descriptionKey="general.contact.address" />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <StaticInfo svg={Address} descriptionKey="general.contact.address2" />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <StaticInfo svg={TelFax} descriptionKey="general.contact.telFax" />
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs={12} md={4} sx={{ display: "flex", justifyContent: { md: "flex-end" } }}>
                    <Box sx={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-start" }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                            {translationService.translate("HelpPage.ourProducts.title")}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2 }}>
                            <ProductLink
                                title="HelpPage.ourProducts.smartManager.title"
                                description="HelpPage.ourProducts.smartManager.description"
                                image="/images/sarida-smartmanager.png"
                                href={configUrl("platform_url")}
                            />
                            <ProductLink
                                title="HelpPage.ourProducts.smartInspector.title"
                                description="HelpPage.ourProducts.smartInspector.description"
                                image="/images/sarida-smartinspector.png"
                                href={configUrl("inspector_url")}
                            />
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            <Box sx={{ mt: 2, px: { xs: 2, md: 6 }, textAlign: "center" }}>
                <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                    {translationService.translate("HelpPage.contact.text")}
                </Typography>
                <TermsAndConditions />
            </Box>
        </Box>
    );
});
